import type { Config } from 'tailwindcss';
import formsPlugin from '@tailwindcss/forms';

export default {
  content: ['./src/**/*.{html,js,svelte,ts}'],
  theme: {
    colors: {
      white: '#FFF',
      black: '#000',
      primary: {
        950: '#660022',
        900: '#903',
        800: '#C04',
        700: '#E5004D',
        600: '#F05',
        500: '#FF1A66',
        400: '#F37',
        300: '#FF5990',
        200: '#FF8CB3',
        100: '#FCD',
        50: '#FFE5EE',
        25: '#FFF2F6',
        shadow: '#FFC9DE',
        visited: '#8A8A8A',
      },
      gray: {
        950: '#0C111D',
        900: '#101828',
        800: '#1D2939',
        700: '#344054',
        600: '#475467',
        500: '#667085',
        400: '#98A2B3',
        300: '#D0D5DD',
        200: '#EAECF0',
        100: '#F2F4F7',
        50: '#F9FAFB',
        25: '#FCFCFD',
      },
      violet: {
        950: '#2E125E',
        900: '#491C96',
        800: '#5720B7',
        700: '#6927DA',
        600: '#7839EE',
        500: '#875BF7',
        400: '#A48AFB',
        300: '#C3B5FD',
        200: '#DDD6FE',
        100: '#ECE9FE',
        50: '#F5F3FF',
        25: '#FBFAFF',
      },
      purple: {
        950: '#27115F',
        900: '#3E1C96',
        800: '#4A1FB8',
        700: '#5925DC',
        600: '#6938EF',
        500: '#7A5AF8',
        400: '#9B8AFB',
        300: '#BDB4FE',
        200: '#D9D6FE',
        100: '#EBE9FE',
        50: '#F4F3FF',
        25: '#FAFAFF',
      },
      fuchsia: {
        950: '#47104C',
        900: '#6F1877',
        800: '#821890',
        700: '#9F1AB1',
        600: '#BA24D5',
        500: '#D444F1',
        400: '#E478FA',
        300: '#EEAAFD',
        200: '#F6D0FE',
        100: '#FBE8FF',
        50: '#FDF4FF',
        25: '#FEFAFF',
      },
      pink: {
        950: '#4E0D30',
        900: '#851651',
        800: '#9E165F',
        700: '#C11574',
        600: '#DD2590',
        500: '#EE46BC',
        400: '#F670C7',
        300: '#FAA7E0',
        200: '#FCCEEE',
        100: '#FCE7F6',
        50: '#FDF2FA',
        25: '#FEF6FB',
      },
      error: {
        100: '#FEE4E2',
        500: '#F04438',
      },
      warning: {
        300: '#FEC84B',
        500: '#F7900',
      },
      success: {
        500: '#17B26A',
      },
      // legacy sweat colours
      'sweat-orange': '#FF3B30',
      'sweat-green': '#4CD964',
      'sweat-gray': {
        400: '#667085',
        60: '#242933',
        50: '#475266',
        40: '#626D83',
        30: '#808A9F',
        20: '#A1AABC',
        10: '#C4CBD8',
        5: '#D7DCE7',
        0: '#ECEEF4',
      },
      'sweat-pink': {
        100: '#80002A',
        90: '#990033',
        80: '#B3003B',
        70: '#CC0044',
        60: '#E5004C',
        50: '#FF0055',
        40: '#FF266F',
        30: '#FF4C89',
        20: '#FF73A4',
        10: '#FF9BBF',
        5: '#FFB4CE',
        0: '#FFCCDD',
      },
      aubergine: {
        100: '#210826',
        90: '#380F3C',
        80: '#511951',
        70: '#672563',
        60: '#7D3373',
        50: '#924382',
        40: '#A85590',
        30: '#BE699F',
        20: '#D480AF',
        10: '#E998BF',
        5: '#F4A5C5',
        0: '#FFB3CC',
        button: '#551DC4',
        hover: '#3E009E',
        shadow: '#9863ED',
      },
      'sweat-purple': {
        100: '#05000F',
        90: '#100030',
        80: '#1B0051',
        70: '#260072',
        60: '#320092',
        50: '#3D00B3',
        40: '#551DC4',
        30: '#713ED4',
        20: '#9065E5',
        10: '#B290F6',
        5: '#C7AEFA',
        0: '#DDCCFF',
      },
    },
    maxWidth: {
      none: 'none',
      '768': '48rem',
      '800': '50rem',
      '832': '52rem',
      '960': '60rem',
      '1008': '63rem',
      '1024': '64rem',
      '1280': '80rem',
    },
    borderRadius: {
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      xxl: '1rem',
      full: '100px',
    },
    boxShadow: {
      button: '0px 0px 0px 4px rgb(0 0 0 / 0.05);',
    },
    fontFamily: {
      sans: ['gilroy', 'system-ui', 'sans-serif'],
      display: ['tuskergrotesk', 'sans-serif'],
    },
    fontSize: {
      xs: [
        '0.75rem',
        {
          lineHeight: '1.125rem',
        },
      ],
      sm: [
        '0.875rem',
        {
          lineHeight: '1.25rem',
        },
      ],
      md: [
        '1rem',
        {
          lineHeight: '1.5rem',
        },
      ],
      lg: [
        '1.125rem',
        {
          lineHeight: '1.75rem',
        },
      ],
      xl: [
        '1.25rem',
        {
          lineHeight: '1.875rem',
        },
      ],
      xxl: [
        '1.5rem',
        {
          lineHeight: '2rem',
        },
      ],
      '5xl': [
        '3rem',
        {
          lineHeight: '60px',
        },
      ],
      'display-xxs': [
        '1.5rem',
        {
          lineHeight: '2rem',
          letterSpacing: '-0.02em',
        },
      ],
      'display-xs': [
        '1.75rem',
        {
          lineHeight: '2.125rem',
          letterSpacing: '-0.02em',
        },
      ],
      'display-sm': [
        '2.125rem',
        {
          lineHeight: '2.375rem',
          letterSpacing: '-0.02em',
        },
      ],
      'display-md': [
        '2.25rem',
        {
          lineHeight: '2.75rem',
          letterSpacing: '-0.02em',
        },
      ],
      'display-lg': [
        '3rem',
        {
          lineHeight: '3.75rem',
          letterSpacing: '-0.02em',
        },
      ],
      'display-xl': [
        '3.75rem',
        {
          lineHeight: '4.5rem',
          letterSpacing: '-0.02em',
        },
      ],
      'display-2xl': [
        '4.5rem',
        {
          lineHeight: '5.625rem',
          letterSpacing: '-0.02em',
        },
      ],
      '15': [
        '0.9375rem',
        {
          lineHeight: '1.375rem',
          letterSpacing: '-0.02em',
        },
      ],
      '11': [
        '0.6875rem',
        {
          lineHeight: '1.0365625rem',
        },
      ],
      '17': [
        '1.0625rem',
        {
          lineHeight: '1.375rem',
          letterSpacing: '-0.02em',
        },
      ],
      '30': [
        '1.875rem',
        {
          lineHeight: '2.375rem',
          letterSpacing: '-0.02em',
        },
      ],
    },
    extend: {
      keyframes: {
        scale: {
          '0%': { transform: 'scale(0)' },
          '100%': { transform: 'scale(1)' },
        },
      },
      animation: {
        scale: 'scale 0.3s ease',
      },
    },
  },
  plugins: [formsPlugin],
} satisfies Config;
